import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, Button, Table, Form, Input, Select, DatePicker, Tag, Space, Row, Col,
  Typography, Alert, Modal, Descriptions, Popover, Divider, Statistic, message
} from 'antd';
import { SearchOutlined, ReloadOutlined, InfoCircleOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const INVOICE_STATUS = {
  pending: { text: '待审核', color: 'default' },
  invoicing: { text: '开票中', color: 'processing' },
  rejected: { text: '审核未通过', color: 'error' },
  done: { text: '已开票', color: 'success' },
};

const INVOICE_TYPES = [
  { value: 'normal_e', label: '增值税普通发票(电子)' },
  { value: 'normal_p', label: '增值税普通发票(纸质)' },
  { value: 'special_p', label: '增值税专用发票(纸质)' },
  { value: 'special_e', label: '增值税专用发票(电子)' },
];

const mockData = [
  { key: '1', issuer: '鹤梦云生态平台', applyNo: '475271a7-06e3-4dce', amount: 1000.00, title: '深圳汇云科技有限公司', invoiceType: '增值税普通发票(电子)', receiveType: 'email', receiveInfo: { email: 'sz@example.com' }, applyTime: '2025-06-21 08:50:08', account: 'huiyun_caiwu', status: 'pending' },
  { key: '2', issuer: '鹤梦云生态平台', applyNo: 'C202403782364', amount: 470.03, title: '深圳汇云科技有限公司', invoiceType: '增值税普通发票(纸质)', receiveType: 'mail', receiveInfo: { name: '张冬冬', phone: '0755-12345678', address: '深圳市龙岗区坂田街道星河WORLD TOWER A座' }, applyTime: '2025-05-21 08:50:08', account: 'huiyun_caiwu', status: 'invoicing' },
  { key: '3', issuer: '鹤梦云生态平台', applyNo: 'C202403782364', amount: 970.03, title: '深圳汇云科技有限公司', invoiceType: '增值税专用发票(纸质)', receiveType: 'mail', receiveInfo: { name: '张冬冬', phone: '0755-12345678', address: '深圳市龙岗区坂田街道星河WORLD TOWER A座' }, applyTime: '2025-04-21 08:50:08', account: 'huiyun_caiwu', status: 'rejected', rejectReason: '发票抬头信息有误，请核实后重新申请' },
  { key: '4', issuer: '鹤梦云生态平台', applyNo: '83h23a7-06e3-fkd88', amount: 880.01, title: '深圳汇云科技有限公司', invoiceType: '增值税专用发票(纸质)', receiveType: 'mail', receiveInfo: { name: '张冬冬', phone: '0755-12345678', address: '深圳市龙岗区坂田街道星河WORLD TOWER A座' }, applyTime: '2025-03-21 08:50:08', account: 'huiyun_caiwu', status: 'done' },
  { key: '5', issuer: '鹤梦云生态平台', applyNo: 'aajhsdgjasdk', amount: 999.99, title: '深圳汇云科技有限公司', invoiceType: '增值税专用发票(电子)', receiveType: 'email', receiveInfo: { email: 'sz@example.com' }, applyTime: '2025-02-21 08:50:08', account: 'huiyun_caiwu', status: 'done' },
];

const InvoiceCenter = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [detailVisible, setDetailVisible] = useState(false);
  const [detailRecord, setDetailRecord] = useState(null);

  const receivePopover = (record) => {
    if (record.receiveType === 'email') {
      return (
        <Descriptions column={1} bordered size="small" style={{ width: 300 }}>
          <Descriptions.Item label="电子邮箱">{record.receiveInfo.email}</Descriptions.Item>
        </Descriptions>
      );
    }
    return (
      <Descriptions column={1} bordered size="small" style={{ width: 380 }}>
        <Descriptions.Item label="收件人">{record.receiveInfo.name}</Descriptions.Item>
        <Descriptions.Item label="联系方式">{record.receiveInfo.phone}</Descriptions.Item>
        <Descriptions.Item label="收件地址">{record.receiveInfo.address}</Descriptions.Item>
      </Descriptions>
    );
  };

  const columns = [
    { title: '序号', width: 60, align: 'center', render: (_, __, i) => i + 1 },
    { title: '开票方', dataIndex: 'issuer', width: 120 },
    { title: '申请编号', dataIndex: 'applyNo', width: 180 },
    { title: '开票金额(CNY)', dataIndex: 'amount', width: 140, align: 'right', render: (v) => <Text strong>{v.toFixed(2)}</Text> },
    { title: '发票抬头', dataIndex: 'title', width: 200 },
    { title: '发票类型', dataIndex: 'invoiceType', width: 200 },
    {
      title: '状态', dataIndex: 'status', width: 120,
      render: (v, r) => {
        const s = INVOICE_STATUS[v];
        if (v === 'rejected') {
          return <Popover content={r.rejectReason} title="未通过原因"><Tag color={s.color} style={{ cursor: 'pointer' }}>{s.text} <InfoCircleOutlined /></Tag></Popover>;
        }
        return <Tag color={s.color}>{s.text}</Tag>;
      },
    },
    {
      title: '收票信息', key: 'receive', width: 100, align: 'center',
      render: (_, r) => <Popover content={receivePopover(r)} title="收票信息" trigger="click"><Button type="link" size="small">查看</Button></Popover>,
    },
    { title: '申请时间', dataIndex: 'applyTime', width: 170, sorter: (a, b) => a.applyTime.localeCompare(b.applyTime), showSorterTooltip: { title: '点击排序' } },
    { title: '申请账号', dataIndex: 'account', width: 130 },
    {
      title: '操作', key: 'action', width: 120, fixed: 'right',
      render: (_, r) => (
        <Space size={0} split={<Divider type="vertical" style={{ margin: '0 4px' }} />}>
          <Button type="link" size="small" onClick={() => { setDetailRecord(r); setDetailVisible(true); }}>详情</Button>
          {r.status === 'done' && <Button type="link" size="small" onClick={() => message.success('下载成功')}>下载</Button>}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16 }}><Title level={4}>发票管理</Title></div>

      <Alert type="info" showIcon style={{ marginBottom: 16, borderRadius: 8 }} message={
        <div style={{ fontSize: 13 }}>
          <div>1. 可开票订单来源：平台消费账户充值（主动充值汇款金额）。单个订单不可拆分开票，但多个可合并开票。一般会在收到申请后<Text strong style={{ color: '#fa8c16' }}>3个工作日</Text>内为您开票。</div>
          <div>2. 发票抬头需与认证企业主体名称一致。<Text type="danger">发票抬头需与认证企业主体名称一致</Text>，切勿随意修改。若企业需要变更发票抬头，请先根据变更情况找平台管理员申请认证主体变更，以免影响开票过程。</div>
          <div>3. 一张发票只能有一个发票类目。</div>
        </div>
      } />

      {/* 可开票金额 + 发票信息 */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={14}>
          <Card bordered={false} style={{ height: '100%' }}>
            <div style={{ marginBottom: 4, fontWeight: 500 }}>可开票金额</div>
            <Statistic value={10512.00} prefix="¥" precision={2} valueStyle={{ color: '#1677ff', fontSize: 32, fontWeight: 600 }} />
            <div style={{ marginTop: 12, color: '#999', fontSize: 13 }}>
              累计充值：¥ 0 · 历史已开票金额：¥ 2.2 · 开票申请中金额：¥ 0
            </div>
            <Button type="primary" style={{ marginTop: 16 }} onClick={() => navigate('/cost/invoices/apply')}>申请开票</Button>
          </Card>
        </Col>
        <Col span={10}>
          <Card bordered={false} style={{ height: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
              <span style={{ fontWeight: 500 }}>发票信息与接收方式</span>
              <Button type="link" size="small" onClick={() => navigate('/cost/invoices/manage')}>去管理 &gt;</Button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13 }}>
              <div><Text type="secondary">发票抬头：</Text>深圳汇云科技有限公司</div>
              <div><Text type="secondary">发票类型：</Text>增值税普通发票</div>
              <div><Text type="secondary">默认邮箱：</Text>sz@example.com</div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* 查询条件 */}
      <Card bordered={false} style={{ marginBottom: 8, borderRadius: 12 }} bodyStyle={{ padding: '12px 0' }}>
        <Form form={form} layout="inline" style={{ flexWrap: 'wrap', gap: '8px 0' }}>
          <Form.Item name="applyNo" style={{ marginRight: 8 }}>
            <Input placeholder="申请编号" allowClear style={{ width: 180 }} />
          </Form.Item>
          <Form.Item name="orderNo" style={{ marginRight: 8 }}>
            <Input placeholder="订单号" allowClear style={{ width: 180 }} />
          </Form.Item>
          <Form.Item name="dateRange" style={{ marginRight: 8 }}>
            <RangePicker placeholder={['开始时间', '结束时间']} />
          </Form.Item>
          <Form.Item name="invoiceType" style={{ marginRight: 8 }}>
            <Select placeholder="发票类型" allowClear style={{ width: 180 }} options={INVOICE_TYPES} />
          </Form.Item>
          <Form.Item name="status" style={{ marginRight: 8 }}>
            <Select placeholder="开票状态" allowClear style={{ width: 130 }}>
              {Object.entries(INVOICE_STATUS).map(([k, v]) => <Option key={k} value={k}>{v.text}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" icon={<SearchOutlined />}>查询</Button>
              <Button icon={<ReloadOutlined />} onClick={() => form.resetFields()}>重置</Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      {/* 列表 */}
      <Card bordered={false}>
        <Table columns={columns} dataSource={mockData} rowKey="key" scroll={{ x: 'max-content' }}
          pagination={{ showSizeChanger: true, showTotal: (t) => `共 ${t} 条记录` }} style={{ whiteSpace: 'nowrap' }} />
      </Card>

      {/* 详情弹窗 */}
      <Modal title="发票详情" open={detailVisible} onCancel={() => setDetailVisible(false)} footer={null} width={640}>
        {detailRecord && (
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label="申请编号">{detailRecord.applyNo}</Descriptions.Item>
            <Descriptions.Item label="开票方">{detailRecord.issuer}</Descriptions.Item>
            <Descriptions.Item label="开票金额">¥ {detailRecord.amount.toFixed(2)}</Descriptions.Item>
            <Descriptions.Item label="发票类型">{detailRecord.invoiceType}</Descriptions.Item>
            <Descriptions.Item label="发票抬头" span={2}>{detailRecord.title}</Descriptions.Item>
            <Descriptions.Item label="申请时间">{detailRecord.applyTime}</Descriptions.Item>
            <Descriptions.Item label="申请账号">{detailRecord.account}</Descriptions.Item>
            <Descriptions.Item label="状态" span={2}>
              <Tag color={INVOICE_STATUS[detailRecord.status].color}>{INVOICE_STATUS[detailRecord.status].text}</Tag>
              {detailRecord.status === 'rejected' && <Text type="danger" style={{ marginLeft: 8 }}>{detailRecord.rejectReason}</Text>}
            </Descriptions.Item>
            <Descriptions.Item label="收票方式" span={2}>
              {detailRecord.receiveType === 'email'
                ? `电子邮箱：${detailRecord.receiveInfo.email}`
                : `邮寄：${detailRecord.receiveInfo.name} / ${detailRecord.receiveInfo.phone} / ${detailRecord.receiveInfo.address}`
              }
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default InvoiceCenter;
